import { Thunk } from 'frontend/@types';
import * as React from 'react';
import { $, T } from 'frontend';
import {
  canonicalizePath,
  downloadBlobForUser,
  dropboxErrorMessage,
  ensureExists,
  getDirName,
  isSameFileVersion,
  getGeneration,
  getPathFileName,
  insertTextAtLine,
  pathJoin,
  formatMegabytes,
  getZipJs,
  processInChunks,
  splitOutFileExtension,
} from 'frontend/utils';
import * as Plain from './plain';
import { FilesIndex, tryUpgradeIndexJSON } from 'frontend/logic/files-index';
import { FileStoreError } from 'frontend/logic/file-store';
import { IDBError } from 'frontend/logic/file-store/indexeddb-fs';
import {
  compareTracksDefault,
  IMPORTABLE_AUDIO_EXTENSIONS,
  matchFolderArtworkFilename,
  type ImportQualitySettings,
  getImportedExtension,
} from 'shared/music';
import type {
  AddTracksResponse,
  WriteFolderArtworkResponse,
} from 'shared/@types/shared';

function getMetadataFromCache(
  cache: T.ListFilesCache,
  folder: string,
  path: string,
) {
  return cache.get(folder)?.files.find((entry) => entry.path === path);
}

/**
 * This file contains all of the thunk actions, that contain extra logic,
 * such as conditional dispatches, and multiple async calls.
 */

const DEFAULT_MESSAGE_DELAY = 3000;
const pendingMessageTimeouts = new Map<number, ReturnType<typeof setTimeout>>();

/**
 * Plain actions defined in thunks.tsx should either be APICalls, or internal.
 * These will all be collected on the global `T` export in `src`.
 */
export type PlainActions =
  // See PlainActions in src/frontent/@types/index.ts for details on this next line.
  T.Values<{
    [FnName in keyof typeof PlainInternal]: ReturnType<
      (typeof PlainInternal)[FnName]
    >;
  }>;

/**
 * These should only be used internally in thunks.
 */
const PlainInternal = {
  addMessage(
    message: React.ReactNode,
    generation: number,
    progress?: T.MessageProgress,
  ) {
    return { type: 'add-message' as const, message, generation, progress };
  },

  dismissAllMessages() {
    return { type: 'dismiss-all-messages' as const };
  },

  listFilesRequested(path: string) {
    return { type: 'list-files-requested' as const, path };
  },

  listFilesReceived(path: string, files: T.FolderListing, isCache: boolean) {
    return { type: 'list-files-received' as const, path, files, isCache };
  },

  listFilesError(path: string, error: string) {
    return { type: 'list-files-error' as const, path, error };
  },

  moveFileRequested(path: string) {
    return { type: 'move-file-requested' as const, path };
  },

  moveFileDone(oldPath: string, metadata: T.FileMetadata | T.FolderMetadata) {
    return { type: 'move-file-done' as const, oldPath, metadata };
  },

  saveFileDone(path: string) {
    return { type: 'save-file-done' as const, path };
  },

  deleteFileDone(
    metadata: T.FileMetadata | T.FolderMetadata,
    folder: string,
    fileFocus: string | null,
  ) {
    return { type: 'delete-file-done' as const, metadata, folder, fileFocus };
  },

  downloadFileRequested(path: string) {
    return { type: 'download-file-requested' as const, path };
  },

  downloadFileReceived(path: string, file: T.TextFile) {
    return { type: 'download-file-received' as const, path, file };
  },

  downloadFileError(path: string, error: string) {
    return { type: 'download-file-error' as const, path, error };
  },

  downloadBlobRequested(path: string) {
    return { type: 'download-blob-requested' as const, path };
  },

  downloadBlobReceived(path: string, blobFile: T.BlobFile) {
    return { type: 'download-blob-received' as const, path, blobFile };
  },

  fileIndexReceived(filesIndex: FilesIndex) {
    return { type: 'files-index-received' as const, filesIndex };
  },

  undoLearnStem(stem: string) {
    return { type: 'undo-learn-stem' as const, stem };
  },

  undoIgnoreStem(stem: string) {
    return { type: 'undo-ignore-stem' as const, stem };
  },

  nextSentence(stem: T.Stem, direction: -1 | 1) {
    return {
      type: 'next-sentence' as const,
      direction,
      stem,
    };
  },

  setMusicTracks(
    tracks: T.TrackMetadata[],
    needsRescan: boolean,
    servedVersion: number | null,
    panelSelections: Partial<Record<T.MusicPanelType, string[]>>,
  ) {
    return {
      type: 'set-music-tracks' as const,
      tracks,
      needsRescan,
      servedVersion,
      panelSelections,
    };
  },

  loadMusicPlayback(path: string, selectedTrackPath?: string) {
    return {
      type: 'music-playback-load' as const,
      path,
      selectedTrackPath,
    };
  },

  setMusicPlaybackQueue(
    tracks: T.TrackMetadata[],
    panelSelections: Partial<Record<T.MusicPanelType, string[]>>,
  ) {
    return {
      type: 'set-music-playback-queue' as const,
      tracks,
      panelSelections,
    };
  },
};

/**
 * Sets the current track metadata, either initially, or after editing an individual
 * piece of metadata. Panel selections are intentionally preserved here. Selectors
 * apply only the currently valid portion of those selections when deriving panel
 * items and track lists.
 */
export function setMusicTracks(
  tracks: T.TrackMetadata[],
  needsRescan: boolean,
  servedVersion: number | null = null,
): Thunk {
  return (dispatch, getState) => {
    const existingSelections = $.getMusicPanelSelections(getState());

    dispatch(
      PlainInternal.setMusicTracks(
        tracks,
        needsRescan,
        servedVersion,
        existingSelections,
      ),
    );
  };
}

export function musicPlaybackLoad(path: string): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const selectedPaths = $.getMusicSelectedTrackPaths(state);
    let selectedTrackPath: string | undefined;
    if (
      selectedPaths.length === 1 &&
      selectedPaths[0] === $.getMusicPlaybackTrackPath(state) &&
      $.getFilteredMusicTracks(state).some(
        (track) => track.path === selectedPaths[0],
      )
    ) {
      selectedTrackPath = path;
    }
    dispatch(PlainInternal.loadMusicPlayback(path, selectedTrackPath));
  };
}

export function setMusicPlaybackQueue(tracks: T.TrackMetadata[]): Thunk {
  return (dispatch, getState) => {
    dispatch(
      PlainInternal.setMusicPlaybackQueue(
        tracks,
        $.getMusicPanelSelections(getState()),
      ),
    );
  };
}

export function listFiles(path = ''): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    let dropboxPath = path;
    if (path === '/') {
      // Dropbox doesn't like the root `/`.
      dropboxPath = '';
    }
    const fileStore = $.getCurrentFS(getState());

    dispatch(PlainInternal.listFilesRequested(path));

    if (fileStore.cache) {
      try {
        const offlineListing = await fileStore.cache.listFiles(path);
        if (offlineListing) {
          dispatch(
            PlainInternal.listFilesReceived(
              path,
              offlineListing,
              /* isCache */ true,
            ),
          );
        }
      } catch (error) {
        if (error instanceof IDBError) {
          error.cacheLog();
        } else {
          console.error(error);
        }
      }
    }

    try {
      const files = await fileStore.listFiles(dropboxPath);
      dispatch(
        PlainInternal.listFilesReceived(path, files, /* isCache */ false),
      );
    } catch (response) {
      dispatch(PlainInternal.listFilesError(path, String(response)));
    }
  };
}

export function downloadFile(path: string): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    dispatch(PlainInternal.downloadFileRequested(path));

    const handleFile = (file: T.TextFile) => {
      dispatch(PlainInternal.downloadFileReceived(path, file));

      // For things like back and next, ensure we have a copy of the prev/next
      // tracks in the folder.
      const listFilesCache = $.getListFilesCache(getState());
      const folder = getDirName(path);
      if (!listFilesCache.get(folder)) {
        dispatch(listFiles(folder)).catch((error) => {
          console.error('Failed to list files after downloading file', error);
        });
      }
    };

    let cachedFile: T.TextFile | undefined;
    const fileStore = $.getCurrentFS(getState());

    if (fileStore.cache) {
      try {
        cachedFile = await fileStore.cache.loadText(path);
        handleFile(cachedFile);
      } catch (error) {
        if (error instanceof IDBError) {
          error.cacheLog();
        } else {
          console.error(error);
        }
      }
    }

    // Kick off the request, even if an offline version was found.
    try {
      const { text, metadata } = await fileStore.loadText(path);
      // The file blob was left off of this type.
      if (cachedFile && isSameFileVersion(cachedFile.metadata, metadata)) {
        // The files are the same.
        return;
      }

      handleFile({
        metadata,
        text,
      });
    } catch (error) {
      dispatch(
        PlainInternal.downloadFileError(
          path,
          (error as FileStoreError).toString(),
        ),
      );
    }
  };
}

export function downloadBlob(path: string): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    dispatch(PlainInternal.downloadBlobRequested(path));

    const folder = getDirName(path);

    const handleBlob = (blobFile: T.BlobFile) => {
      dispatch(PlainInternal.downloadBlobReceived(path, blobFile));
    };

    // For things like back and next, ensure we have a copy of the prev/next
    // tracks in the folder.
    const getFreshFileMetadata = async () => {
      if ($.getListFilesCache(getState()).get(folder)?.isCache !== false) {
        await dispatch(listFiles(folder));
      }
      const listing = $.getListFilesCache(getState()).get(folder);
      if (listing?.isCache === false) {
        return getMetadataFromCache(
          $.getListFilesCache(getState()),
          folder,
          path,
        );
      }
      return undefined;
    };

    const fileStore = $.getCurrentFS(getState());
    let cachedBlob: T.BlobFile | undefined;
    if (fileStore.cache) {
      try {
        cachedBlob = await fileStore.cache.loadBlob(path);
        handleBlob(cachedBlob);
      } catch (error) {
        console.error('Error with indexeddb', error);
      }
    }

    if (cachedBlob) {
      const current = await getFreshFileMetadata();
      if (
        current?.type === 'file' &&
        isSameFileVersion(cachedBlob.metadata, current)
      ) {
        return;
      }
    }

    try {
      handleBlob(await fileStore.loadBlob(path));
    } catch (error) {
      dispatch(
        PlainInternal.downloadFileError(path, dropboxErrorMessage(error)),
      );
    }

    if (!cachedBlob) {
      await getFreshFileMetadata();
    }
  };
}

export function saveTextFile(path: string, text: string): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    const fileStore = $.getCurrentFS(getState());
    const fileStoreDisplayName = $.getFileStoreDisplayName(getState());

    const messageGeneration = dispatch(
      addMessage({
        message: (
          <>
            Saving <code>{path}</code>
          </>
        ),
      }),
    );
    try {
      const metadata = await fileStore.saveText(path, 'overwrite', text);
      dispatch(
        PlainInternal.downloadFileReceived(path, {
          metadata,
          text,
        }),
      );
      dispatch(
        addMessage({
          message: (
            <>
              Saved <code>{path}</code>
            </>
          ),
          generation: messageGeneration,
          timeout: true,
        }),
      );
    } catch (error) {
      dispatch(
        addMessage({
          message: (
            <>
              Unable to save <code>{path}</code>
            </>
          ),
          generation: messageGeneration,
        }),
      );
      console.error(error);
      throw new Error(`Unable to save the file with ${fileStoreDisplayName}.`);
    }
  };
}

export function moveFile(
  fromPath: string,
  toPath: string,
  isBatch = false,
): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    toPath = canonicalizePath(toPath);
    const fileStore = $.getCurrentFS(getState());
    const fileStoreDisplayName = $.getFileStoreDisplayName(getState());
    const name = getPathFileName(toPath);

    dispatch(PlainInternal.moveFileRequested(fromPath));

    // Do not send update messages for batch moves.
    const generation = isBatch
      ? undefined
      : dispatch(
          addMessage({
            message: (
              <>
                Moving file <code>{name}</code>
              </>
            ),
          }),
        );

    try {
      const metadata = await fileStore.move(fromPath, toPath);

      dispatch(PlainInternal.moveFileDone(fromPath, metadata));

      const fromDir = getDirName(fromPath);
      const toDir = getDirName(toPath);
      if (fromDir !== toDir) {
        await Promise.all([
          dispatch(listFiles(fromDir)),
          dispatch(listFiles(toDir)),
        ]);
      }

      if (generation !== undefined) {
        dispatch(
          addMessage({
            message: (
              <>
                Moved <code>{toPath}</code>
              </>
            ),
            generation,
            timeout: true,
          }),
        );
      }
    } catch (error) {
      if (generation !== undefined) {
        dispatch(
          addMessage({
            message: (
              <>
                Unable to move <code>{toPath}</code>
              </>
            ),
            generation,
          }),
        );
      }
      console.error(error);
      throw new Error(`Unable to save the file with ${fileStoreDisplayName}.`);
    }
  };
}

/**
 * Move one or more files/folders into a destination folder.
 */
export function moveFiles(
  fromPaths: string[],
  destinationFolder: string,
): Thunk<Promise<void>> {
  return async (dispatch) => {
    if (fromPaths.length === 0) {
      return;
    }

    const targetFolder = canonicalizePath(destinationFolder);
    const moves = fromPaths
      .map((fromPath) => ({
        fromPath,
        toPath: canonicalizePath(
          pathJoin(targetFolder, getPathFileName(fromPath)),
        ),
      }))
      // Skip no-op moves, e.g. dropping a selection back into its own folder.
      .filter(({ fromPath, toPath }) => toPath !== fromPath);

    if (moves.length === 0) {
      return;
    }

    const isBatch = moves.length > 1;
    let batchGeneration: number | undefined;
    let failureCount = 0;
    const movedNames: string[] = [];

    for (let i = 0; i < moves.length; i++) {
      const { fromPath, toPath } = moves[i];

      if (isBatch) {
        batchGeneration = dispatch(
          addMessage({
            message: (
              <>
                Moving {i + 1}/{moves.length} items
              </>
            ),
            progress: { loaded: i, total: moves.length },
            generation: batchGeneration,
          }),
        );
      }

      try {
        await dispatch(moveFile(fromPath, toPath, isBatch));
        movedNames.push(getPathFileName(toPath));
      } catch (error) {
        failureCount += 1;
        console.error(error);
      }
    }

    if (isBatch) {
      const succeeded = moves.length - failureCount;
      dispatch(
        addMessage({
          message:
            failureCount > 0 ? (
              <>
                Moved {succeeded} of {moves.length} items
              </>
            ) : (
              <>Moved {moves.length} items</>
            ),
          generation: batchGeneration,
          timeout: failureCount === 0,
        }),
      );
    }

    if (movedNames.length) {
      dispatch(Plain.setFileSelection(targetFolder, movedNames));
      dispatch(
        Plain.changeFileFocus(targetFolder, movedNames[movedNames.length - 1]),
      );
    }
  };
}

/**
 * Rename a file, optionally renaming other selected files at the same time.
 * This follows the Windows Explorer convention for renaming multiple
 * selected files at once: the edited file gets the exact typed name, and
 * each other selected file gets the same base name with a counter appended,
 * e.g. "Song (1).md", "Song (2).md".
 */
export function renameFiles(
  file: T.FileMetadata | T.FolderMetadata,
  newName: string,
  siblingFiles: Array<T.FileMetadata | T.FolderMetadata>,
): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    const folder = getDirName(file.path);

    if (newName === file.name && siblingFiles.length === 0) {
      // Nothing to rename.
      dispatch(Plain.stopRenameFile());
      return;
    }

    let listing = $.getListFilesCache(getState()).get(folder)?.files;
    if (!listing) {
      await dispatch(listFiles(folder));
      listing = $.getListFilesCache(getState()).get(folder)?.files;
    }

    // Files being renamed are freeing up their current name, so they
    // shouldn't count as a collision against themselves.
    const renamedPaths = new Set(
      [file, ...siblingFiles].map((entry) => entry.path),
    );
    const existingNames = new Set(
      (listing ?? [])
        .filter((entry) => !renamedPaths.has(entry.path))
        .map((entry) => entry.name),
    );

    if (existingNames.has(newName)) {
      dispatch(
        addMessage({
          message: (
            <>
              A file named <code>{newName}</code> already exists in this folder.
            </>
          ),
          timeout: true,
        }),
      );
      return;
    }

    existingNames.add(newName);
    const { baseName, extension } = splitOutFileExtension(newName);
    const renames: Array<{ fromPath: string; toPath: string }> = [
      { fromPath: file.path, toPath: pathJoin(folder, newName) },
    ];

    for (const sibling of siblingFiles) {
      let counter = 1;
      let candidate = `${baseName} (${counter})${extension}`;
      while (existingNames.has(candidate)) {
        counter += 1;
        candidate = `${baseName} (${counter})${extension}`;
      }
      existingNames.add(candidate);
      renames.push({
        fromPath: sibling.path,
        toPath: pathJoin(folder, candidate),
      });
    }

    const isBatch = renames.length > 1;
    let batchGeneration: number | undefined;
    let failureCount = 0;
    const renamedNames: string[] = [];

    for (let i = 0; i < renames.length; i++) {
      const { fromPath, toPath } = renames[i];

      if (isBatch) {
        batchGeneration = dispatch(
          addMessage({
            message: (
              <>
                Renaming {i + 1}/{renames.length} items
              </>
            ),
            progress: { loaded: i, total: renames.length },
            generation: batchGeneration,
          }),
        );
      }

      try {
        await dispatch(moveFile(fromPath, toPath, isBatch));
        renamedNames.push(getPathFileName(toPath));
      } catch (error) {
        failureCount += 1;
        console.error(error);
      }
    }

    if (isBatch) {
      const succeeded = renames.length - failureCount;
      dispatch(
        addMessage({
          message:
            failureCount > 0 ? (
              <>
                Renamed {succeeded} of {renames.length} items
              </>
            ) : (
              <>Renamed {renames.length} items</>
            ),
          generation: batchGeneration,
          timeout: failureCount === 0,
        }),
      );
    }

    if (renamedNames.length) {
      dispatch(Plain.setFileSelection(folder, renamedNames));
      dispatch(
        Plain.changeFileFocus(folder, renamedNames[renamedNames.length - 1]),
      );
    }
  };
}

/**
 * Handle the pasting of one or more files, with either copy or cut behavior.
 */
export function pasteCopyFile(
  destinationFolder: string,
  clipboard: T.CopyFileState,
): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    const { paths: sourcePaths, isCut } = clipboard;
    if (sourcePaths.length === 0) {
      return;
    }

    const ensureFolderListing = async (folder: string) => {
      let state = getState();
      if (!$.getListFilesCache(state).get(folder)) {
        await dispatch(listFiles(folder));
        state = getState();
      }
      return $.getListFilesCache(getState()).get(folder)?.files ?? null;
    };

    const targetFolder = canonicalizePath(destinationFolder);
    const targetListing = await ensureFolderListing(targetFolder);

    if (!targetListing) {
      dispatch(
        addMessage({
          message: (
            <>
              Unable to access <code>{targetFolder}</code>.
            </>
          ),
        }),
      );
      return;
    }

    // Handle duplicate names within the target folder, taking into account
    // names claimed earlier in this same paste operation.
    // 1. "filename.jpg"
    // 2. "filename copy.jpg"
    // 3. "filename copy 2.jpg"
    const existingNames = new Set(targetListing.map((entry) => entry.name));
    const claimName = (name: string) => {
      let targetName = name;
      if (existingNames.has(name)) {
        const { baseName, extension } = splitOutFileExtension(name);
        let candidate = `${baseName} copy${extension}`;
        let counter = 2;
        while (existingNames.has(candidate)) {
          candidate = `${baseName} copy ${counter}${extension}`;
          counter += 1;
        }
        targetName = candidate;
      }
      existingNames.add(targetName);
      return targetName;
    };

    // With more than one file, collapse per-file progress into a single
    // message that's updated in place, rather than stacking a toast per
    // file. Individual failures still go to the console either way.
    const isBatch = sourcePaths.length > 1;
    const verb = isCut ? 'Moving' : 'Copying';
    const pastVerb = isCut ? 'Moved' : 'Copied';

    const pastedNames: string[] = [];
    let hadSkippedFolderCopy = false;
    let failureCount = 0;
    let batchGeneration: number | undefined;

    for (let i = 0; i < sourcePaths.length; i++) {
      const sourcePath = sourcePaths[i];

      if (isBatch) {
        batchGeneration = dispatch(
          addMessage({
            message: (
              <>
                {verb} {i + 1}/{sourcePaths.length} files
              </>
            ),
            progress: { loaded: i, total: sourcePaths.length },
            generation: batchGeneration,
          }),
        );
      }

      const sourceFolder = getDirName(sourcePath);

      if (!(await ensureFolderListing(sourceFolder))) {
        failureCount += 1;
        console.error(`Unable to locate ${sourcePath} for pasting.`);
        if (!isBatch) {
          dispatch(
            addMessage({
              message: (
                <>
                  Unable to locate <code>{sourcePath}</code> for pasting.
                </>
              ),
              timeout: true,
            }),
          );
        }
        continue;
      }

      let metadata =
        getMetadataFromCache(
          $.getListFilesCache(getState()),
          sourceFolder,
          sourcePath,
        ) ?? null;

      if (!metadata) {
        await dispatch(listFiles(sourceFolder));
        metadata =
          getMetadataFromCache(
            $.getListFilesCache(getState()),
            sourceFolder,
            sourcePath,
          ) ?? null;
      }

      if (!metadata) {
        failureCount += 1;
        console.error(`Unable to locate ${sourcePath} for pasting.`);
        if (!isBatch) {
          dispatch(
            addMessage({
              message: (
                <>
                  Unable to locate <code>{sourcePath}</code> for pasting.
                </>
              ),
              timeout: true,
            }),
          );
        }
        continue;
      }

      const targetName = claimName(metadata.name);
      const targetPath = canonicalizePath(pathJoin(targetFolder, targetName));

      if (isCut) {
        if (targetPath === metadata.path) {
          continue;
        }
        try {
          await dispatch(moveFile(sourcePath, targetPath, isBatch));
          pastedNames.push(targetName);
        } catch (error) {
          failureCount += 1;
          console.error(error);
        }
        continue;
      }

      if (metadata.type === 'folder') {
        hadSkippedFolderCopy = true;
        continue;
      }

      const fileStore = $.getCurrentFS(getState());
      const fileStoreDisplayName = $.getFileStoreDisplayName(getState());
      const singleGeneration = isBatch
        ? undefined
        : dispatch(
            addMessage({
              message: (
                <>
                  Copying <code>{metadata.name}</code>
                </>
              ),
            }),
          );

      try {
        const { blob } = await fileStore.loadBlob(sourcePath);
        const savedMetadata = await fileStore.saveBlob(
          targetPath,
          'overwrite',
          blob,
        );
        pastedNames.push(savedMetadata.name);
        if (!isBatch) {
          dispatch(
            addMessage({
              message: (
                <>
                  Pasted <code>{savedMetadata.path}</code>
                </>
              ),
              generation: singleGeneration,
              timeout: true,
            }),
          );
        }
      } catch (error) {
        failureCount += 1;
        console.error(error);
        if (!isBatch) {
          dispatch(
            addMessage({
              message: (
                <>
                  Unable to copy <code>{metadata.name}</code> with{' '}
                  {fileStoreDisplayName}.
                </>
              ),
              generation: singleGeneration,
            }),
          );
        }
      }
    }

    if (isBatch) {
      const succeeded = pastedNames.length;
      const total = sourcePaths.length;
      batchGeneration = dispatch(
        addMessage({
          message:
            failureCount > 0 ? (
              <>
                {pastVerb} {succeeded} of {total} files
              </>
            ) : (
              <>
                {pastVerb} {total} files
              </>
            ),
          generation: batchGeneration,
          timeout: failureCount > 0 ? false : true,
        }),
      );
    }

    if (hadSkippedFolderCopy) {
      dispatch(
        addMessage({
          message: (
            <>Copying folders is not supported yet. Try cutting instead.</>
          ),
          timeout: true,
        }),
      );
    }

    if (!isCut && pastedNames.length) {
      await dispatch(listFiles(targetFolder));
    }

    dispatch(Plain.clearCopyFile());

    if (pastedNames.length) {
      dispatch(Plain.setFileSelection(targetFolder, pastedNames));
      dispatch(
        Plain.changeFileFocus(
          targetFolder,
          pastedNames[pastedNames.length - 1],
        ),
      );
    }
  };
}

interface MessageArgs {
  message: React.ReactNode;
  // Renders a progress bar in the message.
  progress?: T.MessageProgress;
  // A unique generation number to identify a message, useful if
  // the message needs dismissing or can be replaced.
  generation?: number | void;
  // If a number, the number of milliseconds before hiding.
  // If `true`, then use the default delay.
  // If falsey, do not auto-hide the message.
  timeout?: number | boolean;
}

export function addMessage({
  message,
  progress,
  generation = getGeneration(),
  timeout = false,
}: MessageArgs): Thunk<number> {
  return (dispatch) => {
    dispatch(PlainInternal.addMessage(message, generation, progress));

    const existingTimeout = pendingMessageTimeouts.get(generation);
    if (existingTimeout !== undefined) {
      clearTimeout(existingTimeout);
      pendingMessageTimeouts.delete(generation);
    }

    if (timeout) {
      const timeoutId = setTimeout(
        () => {
          pendingMessageTimeouts.delete(generation);
          dispatch(Plain.dismissMessage(generation));
        },
        typeof timeout === 'number' ? timeout : DEFAULT_MESSAGE_DELAY,
      );
      pendingMessageTimeouts.set(generation, timeoutId);
    }
    return generation;
  };
}

export function dismissAllMessages(): Thunk {
  return (dispatch, getState) => {
    const messages = $.getMessages(getState());
    if (messages.length > 0) {
      dispatch(PlainInternal.dismissAllMessages());
    }
  };
}

/**
 * This is useful for testing purposes from the command line.
 *
 * dispatch($.forceExpiration())
 */
export function forceExpiration(): Thunk {
  return (dispatch, getState) => {
    const oauth = $.getDropboxOauth(getState());
    if (!oauth) {
      return;
    }
    dispatch(
      Plain.setDropboxAccessToken(oauth.accessToken, 0, oauth.refreshToken),
    );
  };
}

const hasFailureMap = new Map<string, boolean>();
export function createInitialFiles(): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    if (process.env.NODE_ENV === 'test') {
      return;
    }
    const fileStore = $.getCurrentFS(getState());
    const fsSlug = $.getCurrentFileStoreSlug(getState());
    let hasFailure = hasFailureMap.get(fsSlug);
    const setFailure = (error: any) => {
      hasFailure = true;
      hasFailureMap.set(fsSlug, true);
      console.error(error);
    };
    if (hasFailure) {
      console.error('Attempting to createInitialFiles after failiure');
      return;
    }

    let files;
    if (process.env.SITE === 'floppydisk') {
      files = ['Getting Started.md'];
    } else {
      files = ['Getting Started.chopro'];
    }
    const fsError: string = '';

    for (const file of files) {
      // Load the file locally first.
      let contents: string;
      try {
        const response = await fetch(window.location.origin + '/guide/' + file);
        contents = await response.text();
      } catch (error) {
        setFailure(error);
        continue;
      }

      // Upload it Dropbox.
      try {
        await fileStore.saveText('/' + file, 'add', contents);
        const files = await fileStore.listFiles('/');
        if (files.length === 0) {
          setFailure(new Error('Failed to create files in the file store'));
        }
      } catch (error) {
        setFailure(error);
      }
    }
    if (hasFailure) {
      dispatch(
        addMessage({
          message: `Could not create the initial demo files. ${fsError}`,
        }),
      );
    }
    dispatch(Plain.invalidatePath('/'));
  };
}

export function downloadFileForUser(
  file: T.FileMetadata,
): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    const messageGeneration = dispatch(
      addMessage({
        message: (
          <>
            Downloading <code>{file.name}</code>
          </>
        ),
      }),
    );

    await $.getCurrentFS(getState())
      .loadBlob(file.path)
      .then(
        ({ blob }) => {
          downloadBlobForUser(file.name, blob);

          dispatch(
            addMessage({
              message: (
                <>
                  Downloaded <code>{file.name}</code>
                </>
              ),
              generation: messageGeneration,
              timeout: true,
            }),
          );
        },
        (error) => {
          console.error(error);
          dispatch(
            addMessage({
              message: (
                <>
                  Failed to download <code>{file.name}</code>
                </>
              ),
              generation: messageGeneration,
              timeout: true,
            }),
          );
        },
      );
  };
}

export function downloadFolderForUser(
  file: T.FolderMetadata,
): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    const messageGeneration = dispatch(
      addMessage({
        message: (
          <>
            Zipping <code>{file.name}</code>
          </>
        ),
      }),
    );

    await $.getCurrentFS(getState())
      .compressFolder(file.path)
      .then(
        (blob) => {
          downloadBlobForUser(file.name, blob);

          dispatch(
            addMessage({
              message: (
                <>
                  Downloaded <code>{file.name}</code>
                </>
              ),
              generation: messageGeneration,
              timeout: true,
            }),
          );
        },
        (error) => {
          console.error(error);
          dispatch(
            addMessage({
              message: (
                <>
                  Failed to download <code>{file.name}</code>
                </>
              ),
              generation: messageGeneration,
              timeout: true,
            }),
          );
        },
      );
  };
}

export function deleteFile(
  file: T.FileMetadata | T.FolderMetadata,
): Thunk<Promise<void>> {
  return deleteFiles([file]);
}

/**
 * Delete one or more files/folders by looping over each file.
 */
export function deleteFiles(
  files: Array<T.FileMetadata | T.FolderMetadata>,
): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    if (files.length === 0) {
      return;
    }

    let confirmMessage: string;
    if (files.length > 1) {
      confirmMessage = `Are you sure you want to delete these ${files.length} items?`;
    } else if (files[0].type === 'file') {
      confirmMessage = `Are you sure you want to delete ${files[0].name}?`;
    } else {
      confirmMessage = `Are you sure you want to delete the folder ${files[0].name} and all of its contents?`;
    }

    if (!confirm(confirmMessage)) {
      return;
    }

    const isBatch = files.length > 1;
    const fileStore = $.getCurrentFS(getState());

    let batchGeneration: number | undefined;
    let failureCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      const singleGeneration = isBatch
        ? undefined
        : dispatch(
            addMessage({
              message: (
                <>
                  Deleting <code>{file.name}</code>
                </>
              ),
            }),
          );

      if (isBatch) {
        batchGeneration = dispatch(
          addMessage({
            message: (
              <>
                Deleting {i + 1}/{files.length} items
              </>
            ),
            progress: { loaded: i, total: files.length },
            generation: batchGeneration,
          }),
        );
      }

      // Recomputed every iteration, since earlier deletes in this loop shift
      // the indices of the remaining files.
      const fileFocusIndex = $.getFileFocusIndex(getState());

      try {
        await fileStore.delete(file.path);

        // Adjust the file focus if needed.
        let fileFocus = $.getFileFocus(getState());
        if (fileFocus === file.name) {
          // We deleted the focused file, go ahead and focus the next file.
          const remainingFiles = $.getSearchFilteredFiles(getState());
          fileFocus = null;
          if (remainingFiles) {
            const nextFile = remainingFiles[fileFocusIndex + 1];
            if (nextFile) {
              fileFocus = nextFile.name;
            } else {
              const prevFile = remainingFiles[fileFocusIndex - 1];
              if (prevFile) {
                fileFocus = prevFile.name;
              }
            }
          }
        }

        dispatch(
          PlainInternal.deleteFileDone(file, getDirName(file.path), fileFocus),
        );

        if (!isBatch) {
          dispatch(
            addMessage({
              message: (
                <>
                  Deleted <code>{file.name}</code>
                </>
              ),
              generation: singleGeneration,
              timeout: true,
            }),
          );
        }
      } catch (error) {
        failureCount += 1;
        console.error(error);
        if (!isBatch) {
          dispatch(
            addMessage({
              message: (
                <>
                  Failed to delete <code>{file.name}</code>
                </>
              ),
              generation: singleGeneration,
              timeout: true,
            }),
          );
        }
      }
    }

    if (isBatch) {
      const succeeded = files.length - failureCount;
      dispatch(
        addMessage({
          message:
            failureCount > 0 ? (
              <>
                Deleted {succeeded} of {files.length} items
              </>
            ) : (
              <>Deleted {files.length} items</>
            ),
          generation: batchGeneration,
          timeout: failureCount === 0,
        }),
      );
    }
  };
}

export function loadFilesIndex(
  isSecondAttempt = false,
): Thunk<Promise<FilesIndex | null>> {
  return async (dispatch, getState) => {
    function dispatchError(error: unknown, message: React.ReactNode) {
      console.error(error);
      dispatch(
        addMessage({
          message,
          timeout: true,
        }),
      );
    }

    const fileStore = $.getCurrentFS(getState());

    async function attemptRecreateFile(
      message: string,
    ): Promise<FilesIndex | null> {
      if (!isSecondAttempt && confirm(message)) {
        try {
          await fileStore.delete(FilesIndex.path);
        } catch (error) {
          dispatchError(
            error,
            <>
              Failed to delete <code>{FilesIndex.path}</code>
            </>,
          );
        }
        const filesIndex = await dispatch(loadFilesIndex(true));
        return filesIndex;
      }
      return null;
    }

    let text: string;
    try {
      const response = await fileStore.loadText(FilesIndex.path);
      text = response.text;
    } catch (error: any) {
      if ((error as FileStoreError)?.isNotFound?.()) {
        const filesIndex = new FilesIndex(fileStore, getState());
        dispatch(PlainInternal.fileIndexReceived(filesIndex));
        return filesIndex;
      }

      // This is not a path not found error, so dispatch an error.
      dispatchError(
        error,
        <>
          Failed to load the files index <code>{FilesIndex.path}</code>
        </>,
      );
      return null;
    }

    let json: any;
    try {
      json = JSON.parse(text);
    } catch (error) {
      console.error(error);
      return attemptRecreateFile(
        `Browser Chord's file index "${FilesIndex.path}" appears corrupted, would you like to ` +
          `delete and try recreating it?`,
      );
    }

    const indexJSON = tryUpgradeIndexJSON(json);
    if (indexJSON === null) {
      return attemptRecreateFile(
        `Browser Chord's file index "${FilesIndex.path}" appeared malformed, would you like to ` +
          `delete and try recreating it?`,
      );
    }
    const filesIndex = new FilesIndex(
      fileStore,
      getState(),
      // For some reason type narrowing `T.IndexJSON | null` to `T.IndexJSON` is
      // not working here.
      ensureExists(indexJSON),
    );
    dispatch(PlainInternal.fileIndexReceived(filesIndex));
    return filesIndex;
  };
}

/**
 * @param folder The folder containing the file, it will be placed in folder/assets
 * @param fileName The folder containing the thing.
 * @param folder The folder containing the thing.
 */
export function saveAssetFile(
  folder: string,
  fileName: string,
  contents: Blob,
): Thunk<Promise<string | null>> {
  return async (dispatch, getState) => {
    const fileStore = $.getCurrentFS(getState());
    let path = `${folder}/assets/${fileName}`;

    // See if there is a root asset folder, as this is the preferred place to put assets.
    const rootFolder = folder.split('/')[1];
    if (rootFolder) {
      const files = await fileStore.listFiles('/' + rootFolder);
      if (
        files.find((file) => file.name === 'assets' && file.type === 'folder')
      ) {
        path = `/${rootFolder}/assets/${fileName}`;
      }
    }

    const messageGeneration = dispatch(
      addMessage({
        message: (
          <>
            Saving <code>{path}</code>
          </>
        ),
      }),
    );
    try {
      const metadata = await fileStore.saveBlob(path, 'add', contents);

      dispatch(
        addMessage({
          message: (
            <>
              Saved <code>{path}</code>
            </>
          ),
          generation: messageGeneration,
          timeout: true,
        }),
      );

      // The path will be different if there is a conflict.
      return metadata.path;
    } catch (error) {
      console.error(error);

      dispatch(
        addMessage({
          message: (
            <>
              Unable to save <code>{path}</code>.{' '}
              {(error as FileStoreError)?.toString()}
            </>
          ),
          generation: messageGeneration,
        }),
      );
    }

    return null;
  };
}

/**
 * Inserts a line of text into the active text file.
 */
export function insertTextAtLineInActiveFile(
  lineIndex: number,
  insert: string,
): Thunk<void> {
  return (dispatch, getState) => {
    const path = $.getPath(getState());
    const oldText = $.getActiveFileText(getState());
    const newText = insertTextAtLine(oldText, lineIndex, insert);
    dispatch(Plain.modifyActiveFile(newText, path, true));
  };
}

export function selectNextStem(direction: -1 | 1): Thunk<number> {
  return (dispatch, getState) => {
    const stems = $.getUnknownStems(getState());
    if (!stems) {
      throw new Error('Expected stems when selecting a new stem');
    }
    let stemIndex = $.getSelectedStemIndex(getState()) ?? -1;
    stemIndex += direction;
    // Keep the index in bounds.
    stemIndex = Math.max(0, Math.min(stemIndex, stems.length - 1));
    dispatch(Plain.selectStem(stemIndex));
    return stemIndex;
  };
}

export function ignoreSelectedStem(): Thunk {
  return (dispatch, getState) => {
    const stem = $.getSelectedStem(getState());
    if (stem) {
      dispatch(Plain.ignoreStem(stem.stem));
    }
  };
}

export function learnSelectedStem(): Thunk {
  return (dispatch, getState) => {
    const stem = $.getSelectedStem(getState());
    if (stem) {
      dispatch(Plain.learnStem(stem.stem));
    }
  };
}

export function applyUndo(): Thunk {
  return (dispatch, getState) => {
    const undoList = $.getUndoList(getState());
    const action = undoList[undoList.length - 1];
    if (!action) {
      return;
    }
    switch (action.type) {
      case 'learn-stem':
        dispatch(PlainInternal.undoLearnStem(action.stem));
        break;
      case 'ignore-stem':
        dispatch(PlainInternal.undoIgnoreStem(action.stem));
        break;
      default:
        throw new Error('Unknown undo action: ' + action.type);
    }
  };
}

export function nextSentence(direction: -1 | 1, stemIndex?: number): Thunk {
  return (dispatch, getState) => {
    if (stemIndex === undefined) {
      stemIndex = ensureExists($.getSelectedStemIndex(getState()));
    }
    const stem = ensureExists($.getUnknownStems(getState()))[stemIndex];

    dispatch(PlainInternal.nextSentence(stem, direction));
  };
}

let timeoutId: NodeJS.Timeout;
let unloadHandler: null | (() => string);
let savePromise: Promise<unknown> = Promise.resolve();
let unloadMessageGeneration: null | number = null;

export function languageCoachSaveTimer(): Thunk {
  return (dispatch, getState) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    if (!unloadHandler) {
      unloadHandler = () => {
        unloadMessageGeneration = dispatch(
          addMessage({
            message: (
              <>
                Saving <code>{fullPath}</code>
              </>
            ),
          }),
        );
        return 'There is a pending language coach save, are you sure you want to leave?';
      };
      window.addEventListener('beforeunload', unloadHandler);
    }

    const fileStore = $.getCurrentFS(getState());
    const data = $.getLanguageCoachData(getState());
    const path = $.getLanguageCoachPath(getState());
    const fullPath = pathJoin(path, 'words.json');

    async function saveLanguageCoatchData() {
      try {
        // If there is a previous save attempt, wait for it to be settled.
        await savePromise.catch(() => {});

        // Kick off the save.
        savePromise = fileStore.saveText(
          fullPath,
          'overwrite',
          JSON.stringify(data),
        );

        await savePromise;

        if (unloadMessageGeneration !== null) {
          // We finished saving after a user tried to leave the page.
          dispatch(
            addMessage({
              message: (
                <>
                  Finished saving <code>{fullPath}</code>. Feel free to close
                  the tab.
                </>
              ),
              generation: unloadMessageGeneration,
            }),
          );
        }
      } catch (error) {
        // The save failed, notify the user.
        console.error(error);
        dispatch(
          addMessage({
            message: (
              <>
                Unable to save <code>{fullPath}</code>
              </>
            ),
          }),
        );
      }

      if (unloadHandler) {
        window.removeEventListener('beforeunload', unloadHandler);
        unloadHandler = null;
      }
      unloadMessageGeneration = null;
    }

    timeoutId = setTimeout(saveLanguageCoatchData, 5000);
  };
}

async function extractAndUploadZip(
  dispatch: T.Dispatch,
  getState: T.GetState,
  folderPath: string,
  file: File,
) {
  const fileStore = $.getCurrentFS(getState());
  const zip = await getZipJs();
  const zipReader = new zip.ZipReader(new zip.BlobReader(file));
  const entries = await zipReader.getEntries();
  const fileEntries = entries.filter((entry) => !entry.directory);

  let count = 0;
  let generation: number | void = undefined;
  function updateMessage(message: React.ReactNode) {
    generation = dispatch(
      addMessage({
        message,
        generation,
        progress: { loaded: count, total: fileEntries.length },
      }),
    );
  }

  processInChunks(fileEntries, 5, async (entry) => {
    count++;

    updateMessage(
      <>
        Adding file {count} of {fileEntries.length} from{' '}
        <code>{file.name}</code>.
      </>,
    );

    const writer = new zip.BlobWriter();

    const path = pathJoin(folderPath, entry.filename);
    await fileStore.saveBlob(path, 'add', await entry.getData(writer));
    dispatch(PlainInternal.saveFileDone(path));
  }).then(
    () => {
      dispatch(listFiles(folderPath)).catch((error) => console.error(error));

      updateMessage(
        <>
          Added all files from <code>{file.name}</code>.
        </>,
      );
    },
    () => {
      updateMessage(
        <>
          There was an error adding files from <code>{file.name}</code>.
        </>,
      );
    },
  );
}

/**
 * Upload files into a folder. This function is infallible, and errors will be reported
 * with messages.
 */
export function uploadFilesWithMessages(
  folderPath: string,
  files: FileList,
): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    const fileStore = $.getCurrentFS(getState());

    if (files.length === 1) {
      const [file] = files;
      if (file.type === 'application/zip') {
        const extract = confirm(
          'The zip file will be automatically extracted. Click cancel to upload it instead.',
        );
        if (extract) {
          await extractAndUploadZip(dispatch, getState, folderPath, file);
          return;
        }
      }
    }

    for (const file of files) {
      if (!file.type && file.size === 0) {
        dispatch(
          addMessage({
            message: (
              <>
                Browsers do not support dragging and dropping folders, cannot
                add <code>{file.name}</code>.
              </>
            ),
          }),
        );
        continue;
      }
      const path = pathJoin(folderPath, file.name);
      const messageGeneration = dispatch(
        addMessage({
          message: (
            <>
              Adding <code>{file.name}</code>
            </>
          ),
        }),
      );

      try {
        await fileStore.saveBlob(path, 'add', file);
        dispatch(PlainInternal.saveFileDone(path));
        dispatch(
          addMessage({
            message: (
              <>
                Added <code>{file.name}</code>
              </>
            ),
            generation: messageGeneration,
            timeout: true,
          }),
        );
        dispatch(listFiles(folderPath)).catch((error) => {
          console.error(error);
          dispatch(
            addMessage({
              message: (
                <>
                  Error listing files <code>{folderPath}</code>
                </>
              ),
              timeout: true,
              generation: messageGeneration,
            }),
          );
        });
      } catch (error) {
        console.error(error);
        dispatch(
          addMessage({
            message: (
              <>
                Error saving <code>{path}</code>
              </>
            ),
            timeout: true,
            generation: messageGeneration,
          }),
        );
      }
    }
  };
}

function dedupeStagedFilenames(
  names: string[],
  takenNames: Iterable<string> = [],
): string[] {
  const taken = new Set(takenNames);
  return names.map((name) => {
    if (!taken.has(name)) {
      taken.add(name);
      return name;
    }
    const { baseName, extension } = splitOutFileExtension(name);
    let counter = 2;
    let candidate = `${baseName} (${counter})${extension}`;
    while (taken.has(candidate)) {
      counter += 1;
      candidate = `${baseName} (${counter})${extension}`;
    }
    taken.add(candidate);
    return candidate;
  });
}

interface StagedUploadResult {
  tracks: T.TrackMetadata[];
  duplicateCount: number;
}

async function uploadToStagingPool(
  fileStore: ReturnType<typeof $.getCurrentFS>,
  server: ReturnType<typeof $.getCurrentServer>,
  mp3Files: File[],
  filenames: string[],
  folderArtworkPathByPath: Record<string, string>,
  onProgress: (uploadedBytes: number) => void,
): Promise<StagedUploadResult> {
  const trackPaths: string[] = [];
  let finishedBytes = 0;
  for (let i = 0; i < mp3Files.length; i++) {
    const path = `/.music-staging/${filenames[i]}`;
    await fileStore.saveBlob(path, 'add', mp3Files[i], (bytes) =>
      onProgress(finishedBytes + bytes),
    );
    finishedBytes += mp3Files[i].size;
    trackPaths.push(path);
    onProgress(finishedBytes);
  }

  const res = await fetch(`${server.url}/music/staging-pool`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ trackPaths, folderArtworkPathByPath }),
  });
  if (!res.ok) {
    throw new Error(await res.text());
  }
  return (await res.json()) as T.StagingUploadResponse;
}

async function uploadStagingFolderArtwork(
  fileStore: ReturnType<typeof $.getCurrentFS>,
  artworkFile: File,
): Promise<string> {
  const extension = artworkFile.type === 'image/png' ? 'png' : 'jpg';
  const path = `/.music-staging/.artwork/${crypto.randomUUID()}/Folder.${extension}`;
  await fileStore.saveBlob(path, 'add', artworkFile);
  return path;
}

export function addFilesToStagingPool(
  droppedFiles: Array<{ file: File; sourceFolder: string }>,
): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    const fileStore = $.getCurrentFS(getState());
    const server = $.getCurrentServer(getState());

    const byFolder = new Map<
      string,
      Array<{ file: File; sourceFolder: string }>
    >();
    for (const dropped of droppedFiles) {
      const group = byFolder.get(dropped.sourceFolder) ?? [];
      group.push(dropped);
      byFolder.set(dropped.sourceFolder, group);
    }

    const mp3Files: File[] = [];
    const mp3SourceFolders: string[] = [];
    const folderArtworkBySourceFolder = new Map<string, File>();
    let rejectedCount = 0;
    for (const [sourceFolder, entries] of byFolder) {
      const isImportable = (entry: { file: File }) =>
        IMPORTABLE_AUDIO_EXTENSIONS.some((extension) =>
          entry.file.name.toLowerCase().endsWith(extension),
        );
      const audioEntries = entries.filter(isImportable);
      const otherEntries = entries.filter((entry) => !isImportable(entry));
      for (const entry of audioEntries) {
        mp3Files.push(entry.file);
        mp3SourceFolders.push(sourceFolder);
      }
      const artworkName = matchFolderArtworkFilename(
        otherEntries.map((entry) => entry.file.name),
      );
      const artworkEntry = artworkName
        ? otherEntries.find((entry) => entry.file.name === artworkName)
        : undefined;
      if (artworkEntry) {
        folderArtworkBySourceFolder.set(sourceFolder, artworkEntry.file);
      }
      rejectedCount += otherEntries.length - (artworkEntry ? 1 : 0);
    }

    const reportRejectedFiles = () => {
      if (rejectedCount > 0) {
        dispatch(
          addMessage({
            message: `Skipped ${rejectedCount} non-audio file${rejectedCount === 1 ? '' : 's'}.`,
            timeout: true,
          }),
        );
      }
    };
    if (mp3Files.length === 0) {
      reportRejectedFiles();
      return;
    }

    const reportDuplicates = (count: number) => {
      if (count > 0) {
        dispatch(
          addMessage({
            message: `Skipped ${count} duplicate file${count === 1 ? '' : 's'} — already staged.`,
            timeout: true,
          }),
        );
      }
    };

    const existingPool = $.getMusicStagingPool(getState());
    const takenNames = existingPool.map((track) => getPathFileName(track.path));
    const filenames = dedupeStagedFilenames(
      mp3Files.map((file) => file.name),
      takenNames,
    );

    const folderArtworkPathBySourceFolder = new Map<string, string>();
    for (const [sourceFolder, artworkFile] of folderArtworkBySourceFolder) {
      try {
        folderArtworkPathBySourceFolder.set(
          sourceFolder,
          await uploadStagingFolderArtwork(fileStore, artworkFile),
        );
      } catch (error) {
        console.error(error);
      }
    }
    const folderArtworkPathByStagingPath: Record<string, string> = {};
    for (let i = 0; i < filenames.length; i++) {
      const artworkPath = folderArtworkPathBySourceFolder.get(
        mp3SourceFolders[i],
      );
      if (artworkPath) {
        folderArtworkPathByStagingPath[`/.music-staging/${filenames[i]}`] =
          artworkPath;
      }
    }

    const totalBytes = mp3Files.reduce((sum, file) => sum + file.size, 0);
    const stagingMessage = (uploadedBytes: number) => ({
      message: `Copying ${mp3Files.length} ${mp3Files.length === 1 ? 'track' : 'tracks'}`,
      progress: {
        loaded: uploadedBytes,
        total: totalBytes,
        detail: `${formatMegabytes(uploadedBytes)} of ${formatMegabytes(totalBytes)}`,
      },
    });
    let lastDispatch = 0;
    const messageGeneration = dispatch(addMessage(stagingMessage(0)));

    try {
      const { tracks: addedTracks, duplicateCount } = await uploadToStagingPool(
        fileStore,
        server,
        mp3Files,
        filenames,
        folderArtworkPathByStagingPath,
        (uploadedBytes) => {
          const now = Date.now();
          if (uploadedBytes < totalBytes && now - lastDispatch < 100) {
            return;
          }
          lastDispatch = now;
          dispatch(
            addMessage({
              ...stagingMessage(uploadedBytes),
              generation: messageGeneration,
            }),
          );
        },
      );

      if (addedTracks.length > 0) {
        dispatch(Plain.setMusicStagingPool([...existingPool, ...addedTracks]));
        dispatch(Plain.setMusicStagingView('staging'));
        if (existingPool.length === 0) {
          dispatch(Plain.setMusicSelectedTracks([addedTracks[0].path]));
        }
      }
      dispatch(Plain.dismissMessage(messageGeneration));
      reportRejectedFiles();
      reportDuplicates(duplicateCount);
    } catch (error) {
      console.error(error);
      dispatch(
        addMessage({
          message: 'There was an error staging the dropped files.',
          generation: messageGeneration,
          timeout: true,
        }),
      );
      reportRejectedFiles();
    }
  };
}

export function removeMusicStagingPoolTracks(
  paths: string[],
): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    if (paths.length === 0) {
      return;
    }
    const fileStore = $.getCurrentFS(getState());

    const removed: string[] = [];
    const isBatch = paths.length > 1;
    let messageGeneration: number | undefined;
    for (let i = 0; i < paths.length; i++) {
      if (isBatch) {
        messageGeneration = dispatch(
          addMessage({
            message: `Removing ${i + 1} / ${paths.length} tracks`,
            progress: { loaded: i, total: paths.length },
            generation: messageGeneration,
          }),
        );
      }
      try {
        await fileStore.delete(paths[i]);
        removed.push(paths[i]);
      } catch (error) {
        console.error(error);
      }
    }
    if (messageGeneration !== undefined) {
      dispatch(Plain.dismissMessage(messageGeneration));
    }
    if (removed.length === 0) {
      return;
    }

    const removedSet = new Set(removed);
    const pool = $.getMusicStagingPool(getState());
    const remainingPool = pool.filter((track) => !removedSet.has(track.path));
    dispatch(Plain.setMusicStagingPool(remainingPool));
    dispatch(
      Plain.setMusicSelectedTracks(
        $.getMusicSelectedTrackPaths(getState()).filter(
          (path) => !removedSet.has(path),
        ),
      ),
    );
  };
}

/**
 * Deletes one or more tracks from the music library after a confirm prompt.
 */
export function deleteMusicTracks(paths: string[]): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    if (paths.length === 0) {
      return;
    }
    const pathSet = new Set(paths);
    const tracksToDelete = $.getMusicTracks(getState()).filter((track) =>
      pathSet.has(track.path),
    );
    if (tracksToDelete.length === 0) {
      return;
    }

    const confirmMessage =
      tracksToDelete.length > 1
        ? `Are you sure you want to delete these ${tracksToDelete.length} tracks?`
        : `Are you sure you want to delete ${
            tracksToDelete[0].title ?? getPathFileName(tracksToDelete[0].path)
          }?`;

    if (!confirm(confirmMessage)) {
      return;
    }

    const server = $.getCurrentServer(getState());
    let response: T.DeleteTracksResponse;
    try {
      const res = await fetch(`${server.url}/music/delete-tracks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paths: tracksToDelete.map((track) => track.path),
        }),
      });
      if (!res.ok) {
        throw new Error(await res.text());
      }
      response = (await res.json()) as T.DeleteTracksResponse;
    } catch (error) {
      console.error(error);
      dispatch(
        addMessage({
          message: 'Could not delete the track(s).',
          timeout: true,
        }),
      );
      return;
    }

    const { deleted: deletedPaths, errors } = response;
    const failureCount = errors.length;

    if (deletedPaths.length > 0) {
      const deletedSet = new Set(deletedPaths);
      const remainingTracks = $.getMusicTracks(getState()).filter(
        (track) => !deletedSet.has(track.path),
      );
      dispatch(
        setMusicTracks(
          remainingTracks,
          $.getMusicNeedsRescan(getState()),
          $.getMusicServedIndexVersion(getState()),
        ),
      );
      dispatch(
        Plain.setMusicSelectedTracks(
          $.getMusicSelectedTrackPaths(getState()).filter(
            (path) => !deletedSet.has(path),
          ),
        ),
      );
      const playingTrackPath = $.getMusicPlaybackTrackPath(getState());
      if (playingTrackPath && deletedSet.has(playingTrackPath)) {
        dispatch(Plain.musicPlaybackStop());
      }
    }

    let message: React.ReactNode;
    if (failureCount > 0) {
      message = `Deleted ${deletedPaths.length} track(s), ${failureCount} failed.`;
    } else if (tracksToDelete.length > 1) {
      message = `Deleted ${deletedPaths.length} tracks.`;
    } else {
      message = (
        <>
          Deleted <code>{getPathFileName(tracksToDelete[0].path)}</code>
        </>
      );
    }

    dispatch(addMessage({ message, timeout: true }));
  };
}

export function refreshMusicStagingPool(): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    const server = $.getCurrentServer(getState());
    try {
      const res = await fetch(`${server.url}/music/staging-pool`);
      if (!res.ok) {
        return;
      }
      const tracks = (await res.json()) as T.TrackMetadata[];
      dispatch(Plain.setMusicStagingPool(tracks));
    } catch (error) {
      console.error(error);
    }
  };
}

async function organizeStagedTrack(
  server: ReturnType<typeof $.getCurrentServer>,
  from: string,
  to: string,
  importQuality: ImportQualitySettings,
): Promise<T.OrganizeTrackResponse> {
  const res = await fetch(`${server.url}/music/organize-track`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ from, to, importQuality }),
  });
  if (!res.ok) {
    throw new Error(await res.text());
  }
  return (await res.json()) as T.OrganizeTrackResponse;
}

async function findMusicImportCollisions(
  fileStore: ReturnType<typeof $.getCurrentFS>,
  destinations: Map<string, string>,
): Promise<Set<string>> {
  const collisions = new Set<string>();

  const sourcesByDestination = new Map<string, string[]>();
  for (const [source, destination] of destinations) {
    const sources = sourcesByDestination.get(destination) ?? [];
    sources.push(source);
    sourcesByDestination.set(destination, sources);
  }
  for (const sources of sourcesByDestination.values()) {
    if (sources.length > 1) {
      for (const source of sources) {
        collisions.add(source);
      }
    }
  }

  const listingByFolder = new Map<string, T.FolderListing | null>();
  for (const [source, destination] of destinations) {
    if (collisions.has(source)) {
      continue;
    }
    const folder = getDirName(destination);
    let listing = listingByFolder.get(folder);
    if (listing === undefined) {
      listing = await fileStore.listFiles(folder).catch(() => null);
      listingByFolder.set(folder, listing);
    }
    if (listing?.some((entry) => entry.path === destination)) {
      collisions.add(source);
    }
  }

  return collisions;
}

async function writeStagedFolderArtwork(
  server: ReturnType<typeof $.getCurrentServer>,
  trackPath: string,
  artwork: Blob,
  contentType: 'image/jpeg' | 'image/png',
): Promise<string | null> {
  const res = await fetch(
    `${server.url}/music/artwork?path=${encodeURIComponent(trackPath)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': contentType },
      body: await artwork.arrayBuffer(),
    },
  );
  if (!res.ok) {
    throw new Error(await res.text());
  }
  const data = (await res.json()) as WriteFolderArtworkResponse;
  return data.folderArtworkPath ?? null;
}

export interface MusicImportCommitResult {
  /** Source paths (still staged) that were not moved. */
  collisions: string[];
  /** Source paths (still staged) whose move or conversion errored. */
  failures: string[];
}

export function commitStagingPoolTracks(
  destinations: Record<string, string>,
): Thunk<Promise<MusicImportCommitResult>> {
  return async (dispatch, getState) => {
    const fileStore = $.getCurrentFS(getState());
    const server = $.getCurrentServer(getState());
    const destinationMap = new Map(Object.entries(destinations));
    const importQuality = $.getMusicImportQuality(getState());
    const collisions = await findMusicImportCollisions(
      fileStore,
      new Map(
        [...destinationMap].map(([source, destination]) => {
          const { extension } = splitOutFileExtension(destination);
          return [
            source,
            destination.slice(0, destination.length - extension.length) +
              getImportedExtension(extension, importQuality),
          ];
        }),
      ),
    );

    const pool = $.getMusicStagingPool(getState());
    const tracksToMove = pool.filter(
      (track) => destinationMap.has(track.path) && !collisions.has(track.path),
    );

    const messageGeneration = dispatch(
      addMessage({
        message: `Organizing… 0 / ${tracksToMove.length}`,
        progress: { loaded: 0, total: tracksToMove.length },
      }),
    );
    const movedTracks: T.TrackMetadata[] = [];
    const movedSourcePaths = new Set<string>();
    const stillStagedPaths = new Set(collisions);
    const failures = new Set<string>();
    const destFoldersByStagingArtworkPath = new Map<string, Set<string>>();
    const stagingArtworkPathsByDestFolder = new Map<string, Set<string>>();
    for (let i = 0; i < tracksToMove.length; i++) {
      const track = tracksToMove[i];
      const destination = destinationMap.get(track.path);
      if (!destination) {
        continue;
      }
      try {
        await fileStore.createFolder(getDirName(destination));
        if (importQuality.maximumQuality === 'original') {
          await fileStore.move(track.path, destination);
          movedTracks.push({ ...track, path: destination });
        } else {
          const moved = await organizeStagedTrack(
            server,
            track.path,
            destination,
            importQuality,
          );
          movedTracks.push({ ...track, ...moved });
        }
        movedSourcePaths.add(track.path);
        if (track.folderArtworkPath) {
          const destFolders =
            destFoldersByStagingArtworkPath.get(track.folderArtworkPath) ??
            new Set<string>();
          destFolders.add(getDirName(destination));
          const artworkPaths =
            stagingArtworkPathsByDestFolder.get(getDirName(destination)) ??
            new Set<string>();
          artworkPaths.add(track.folderArtworkPath);
          stagingArtworkPathsByDestFolder.set(
            getDirName(destination),
            artworkPaths,
          );
          destFoldersByStagingArtworkPath.set(
            track.folderArtworkPath,
            destFolders,
          );
        }
      } catch (error) {
        console.error(error);
        failures.add(track.path);
        stillStagedPaths.add(track.path);
      }
      dispatch(
        addMessage({
          message: `Organizing… ${i + 1} / ${tracksToMove.length}`,
          progress: { loaded: i + 1, total: tracksToMove.length },
          generation: messageGeneration,
        }),
      );
    }

    const remainingPool = pool.filter(
      (track) => !movedSourcePaths.has(track.path),
    );

    const folderArtworkPathByDestFolder = new Map<string, string>();
    for (const [
      stagingArtworkPath,
      destFolders,
    ] of destFoldersByStagingArtworkPath) {
      const contentType: 'image/jpeg' | 'image/png' = stagingArtworkPath
        .toLowerCase()
        .endsWith('.png')
        ? 'image/png'
        : 'image/jpeg';
      let artworkBlob: Blob | undefined;
      for (const destFolder of destFolders) {
        if ((stagingArtworkPathsByDestFolder.get(destFolder)?.size ?? 0) > 1) {
          continue;
        }
        const trackInFolder = movedTracks.find(
          (track) => getDirName(track.path) === destFolder,
        );
        if (!trackInFolder) {
          continue;
        }
        try {
          if (!artworkBlob) {
            artworkBlob = (await fileStore.loadBlob(stagingArtworkPath)).blob;
          }
          const folderArtworkPath = await writeStagedFolderArtwork(
            server,
            trackInFolder.path,
            artworkBlob,
            contentType,
          );
          if (folderArtworkPath) {
            folderArtworkPathByDestFolder.set(destFolder, folderArtworkPath);
          }
        } catch (error) {
          console.error(error);
        }
      }
    }
    const movedTracksWithArtwork = movedTracks.map((track) => ({
      ...track,
      folderArtworkPath:
        folderArtworkPathByDestFolder.get(getDirName(track.path)) ?? null,
    }));

    if (movedTracksWithArtwork.length > 0) {
      const existingTracks = $.getMusicTracks(getState());
      const needsRescan = $.getMusicNeedsRescan(getState());
      const servedIndexVersion = $.getMusicServedIndexVersion(getState());

      let indexPatched = false;
      try {
        const res = await fetch(`${server.url}/music/add-tracks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tracks: movedTracksWithArtwork }),
        });
        if (res.ok) {
          const data = (await res.json()) as AddTracksResponse;
          indexPatched = data.index.status === 'updated';
        }
      } catch (error) {
        console.error('Failed to patch the music index after import.', error);
      }

      const mergedTracks = [...existingTracks, ...movedTracksWithArtwork].sort(
        compareTracksDefault,
      );
      dispatch(
        setMusicTracks(
          mergedTracks,
          indexPatched ? needsRescan : true,
          servedIndexVersion,
        ),
      );
    }

    if (movedSourcePaths.size > 0) {
      dispatch(Plain.setMusicStagingPool(remainingPool));
    }

    dispatch(
      addMessage({
        message:
          stillStagedPaths.size === 0
            ? `Organized ${movedTracks.length} track(s).`
            : `Organized ${movedTracks.length} track(s) — ${stillStagedPaths.size} need attention.`,
        generation: messageGeneration,
        timeout: true,
      }),
    );

    return {
      collisions: [...stillStagedPaths].filter((path) => !failures.has(path)),
      failures: [...failures],
    };
  };
}
